/**
 * ``useSystemStatusSnapshot`` — TanStack Query hook over
 * ``GET /api/system/snapshot`` (Flow L; ADR-035 / S6; REQ-STATUS-020).
 *
 * The **full-detail** base for the roll-up (storyboard §6 "snapshot then SSE"):
 * one fetch renders every component's stoplight + inline resource / domain /
 * connection signals.
 *
 * # Freshness (REQ-STATUS-099, 2026-10-04)
 *
 * This response carries **every number on the display**; the SSE frame carries
 * the stoplight only (eight fields, no ``domain`` map, no ``resource`` block, no
 * connection counts — REQ-STATUS-062). So the stream cannot be the sole source
 * of freshness for this query, and until REQ-STATUS-099 nothing else was: it
 * re-fetched on mount and on SSE *reconnect* and at no other time, so a panel
 * left open against a healthy stream showed its mount-time values indefinitely.
 *
 * It now re-reads on the live frame via :func:`useLiveFrameRefresh`, floored at
 * :data:`SNAPSHOT_MIN_REFRESH_MS`. There is still deliberately **no
 * ``refetchInterval``** — a timer would keep reading against a collector that
 * has stopped publishing, where the frame-driven refresh correctly stops and
 * lets the rollup badge go ``Stale`` (REQ-STATUS-016).
 *
 * The reconnect invalidation in :func:`useSystemStatusStream` is retained: it
 * closes the gap across a drop, where no frame arrives to drive a refresh.
 *
 * ⚠ **Mountedness is not visibility, and this query is mounted nearly always.**
 * ``system-status`` ships in the **default layout** (:file:`@/multi-pane/presets`)
 * in a tab group whose active view is the map, and dockview keeps non-active
 * tabs mounted — so this hook runs in essentially every session, including ones
 * where the operator never opened the System panel. The first cut of
 * REQ-STATUS-099 gated the refresh on nothing more than that, and the live pass
 * measured four snapshot reads in 25 s with the panel never opened: every
 * authenticated tab polling, which is precisely the cost profile that argued
 * against widening the SSE frame instead. :func:`useLiveFrameRefresh` now
 * gates itself on :func:`useOwningPanelVisible`, so this query is refreshed
 * only while a panel that renders it is the active tab.
 *
 * **Every authenticated role** (REQ-STATUS-029 as widened by ADR-036 §D-7 /
 * REQ-PEER-042): the BFF gates the endpoint; this hook mirrors the gate so a
 * session carrying no known role never issues the request (defence-in-depth).
 * Response is shape-guarded at the trust boundary (coding-standards §2.2)
 * before it reaches the panel.
 *
 * Cross-references:
 *   - Flow L §1 (role gating, amended 2026-07-24) / §6 (snapshot then SSE).
 *   - ADR-035 D6/D8; ADR-036 §D-7 (all-roles read).
 *   - REQ-STATUS-020 (snapshot endpoint); REQ-STATUS-029 (RBAC);
 *     REQ-PEER-042 (non-privileged read of the peer surface).
 *   - REQ-UI-035 (BFF-only calls); REQ-UI-046 (status palette consumer).
 */
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { ApiError, apiFetch } from "@/lib/api-client";
import { useSession } from "@/auth/session-context";
import type { Role } from "@/auth/session-types";
import type { SystemStatusSnapshot } from "@/system-status/status-model";
import { useLiveFrameRefresh } from "@/system-status/use-live-frame-refresh";

/**
 * Roles permitted to **read** the System Status surface — every authenticated
 * role (REQ-STATUS-029 as widened by ADR-036 §D-7 / REQ-PEER-042, Flow L §1
 * amended 2026-07-24). The display now carries the Peer Manager interfaces, and
 * those are the flows *leaving* the system: an operator who cannot see them
 * cannot notice one running that should not be.
 *
 * This list must stay in step with ``PANEL_ROLES["system-status"]``
 * (:file:`@/multi-pane/registry-types`) — the registry decides whether the panel
 * is offered, this decides whether it renders anything once opened. Widening one
 * without the other leaves a reachable panel that shows only "access restricted".
 *
 * **Read only.** Mutation (the aggregator incident-disable, and the Phase-E
 * peer management affordances) stays ``support_engineer`` + ``admin``; those
 * gates live on their own routes and hooks, not here.
 */
export const SYSTEM_STATUS_READER_ROLES: readonly Role[] = [
  "operator",
  "analyst",
  "support_engineer",
  "admin",
];

/** The TanStack Query key for the snapshot; the SSE hook invalidates it on reconnect. */
export const SYSTEM_STATUS_SNAPSHOT_KEY: readonly unknown[] = ["system-status", "snapshot"];

/**
 * Floor between live-frame-driven re-reads of the snapshot (REQ-STATUS-099).
 *
 * Matched to the default status-emit cadence (``MERIDIAN_STATUS_EMIT_INTERVAL_
 * SECONDS``, 5 s), so at the shipped cadence this re-reads once per frame — the
 * fastest rate at which the underlying ``status.*`` rows actually move. It is a
 * floor rather than a period: a deployment that tunes the cadence *down* gets
 * the same read rate, not a faster one.
 */
export const SNAPSHOT_MIN_REFRESH_MS = 5_000;

/**
 * Fetch the current System Status snapshot.
 *
 * Disabled (no request) for non-authorized sessions. 401/403 are not retried
 * (the BFF's RBAC verdict is authoritative); transient errors retry twice.
 * Re-reads on each live frame, floored at :data:`SNAPSHOT_MIN_REFRESH_MS` — see
 * the module docstring for why that is not a ``refetchInterval``.
 */
export function useSystemStatusSnapshot(): UseQueryResult<SystemStatusSnapshot, ApiError> {
  const session = useSession();
  const enabled = session.hasAnyRole(SYSTEM_STATUS_READER_ROLES);
  const query = useQuery<SystemStatusSnapshot, ApiError>({
    queryKey: SYSTEM_STATUS_SNAPSHOT_KEY,
    queryFn: async ({ signal }) => {
      const raw = await apiFetch<unknown>("/api/system/snapshot", { signal });
      if (!isSnapshot(raw)) {
        console.error(
          "[meridian/system-status/use-system-status-snapshot] response failed shape validation",
          raw,
        );
        throw new ApiError(
          500,
          raw,
          "system-status snapshot response did not match the expected shape",
        );
      }
      return raw;
    },
    enabled,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // Freshness for everything the SSE frame does not carry — the domain metrics,
  // resource block, liveness and connection counts this response holds and the
  // stream omits (REQ-STATUS-099).
  useLiveFrameRefresh({
    queryKey: SYSTEM_STATUS_SNAPSHOT_KEY,
    refetch: query.refetch,
    minIntervalMs: SNAPSHOT_MIN_REFRESH_MS,
    enabled,
  });

  return query;
}

/** Trust-boundary shape guard (coding-standards §2.2; CLAUDE.md §8.1). */
function isSnapshot(value: unknown): value is SystemStatusSnapshot {
  if (typeof value !== "object" || value === null) return false;
  const o = value as Record<string, unknown>;
  return (
    typeof o.network === "string" &&
    typeof o.overall_state === "string" &&
    Array.isArray(o.components) &&
    Array.isArray(o.planes)
  );
}
